import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AgnesInput, AgnesButton, AgnesLabel } from '@agnes/ui';
import { Building2, ChevronDown, Mail } from 'lucide-react';
import ServiceAuthSSO from '@/services/ServiceAuthSSO';

interface TenantOption {
  slug: string;
  display_name: string;
}

// Mensagem genérica única (anti-enumeração): não revela se o e-mail existe.
const MSG_GENERICA =
  'Se o e-mail existir na organização, enviaremos as instruções para redefinir a senha.';

export default function PageForgotPassword() {
  const navigate = useNavigate();
  const [tenants, setTenants] = React.useState<TenantOption[]>([]);
  const [org, setOrg] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [enviado, setEnviado] = React.useState(false);

  React.useEffect(() => {
    ServiceAuthSSO.listTenants()
      .then((list: TenantOption[]) =>
        setTenants([...list].sort((a, b) => a.display_name.localeCompare(b.display_name, 'pt-BR'))),
      )
      .catch(() => setTenants([]));
  }, []);

  const podeEnviar = org.trim() !== '' && email.trim() !== '' && !loading;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeEnviar) return;
    setLoading(true);
    try {
      await ServiceAuthSSO.forgotPassword(org, email);
    } catch {
      // Anti-enumeração: mesmo em erro, mostramos a mensagem genérica.
    } finally {
      setLoading(false);
      setEnviado(true);
    }
  };

  if (enviado) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-lg font-semibold text-agnes-text-primary">Recuperar senha</h1>
          <p className="mt-2 text-sm text-agnes-text-muted">{MSG_GENERICA}</p>
        </div>
        <AgnesButton type="button" variant="primary" onClick={() => navigate('/login')}>
          Voltar ao login
        </AgnesButton>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-agnes-text-primary">Recuperar senha</h1>
        <p className="mt-1 text-sm text-agnes-text-muted">
          Enviaremos um link de recuperação para o seu e-mail.
        </p>
      </div>

      <div>
        <AgnesLabel required>Organização</AgnesLabel>
        <div className="relative">
          <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-agnes-text-muted" />
          <select
            data-testid="tenant-select"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            className={`w-full appearance-none rounded-lg border border-agnes-border bg-agnes-bg-surface py-2.5 pl-10 pr-10 text-sm outline-none transition-colors focus:border-agnes-primary ${org ? 'text-agnes-text-primary' : 'text-agnes-text-muted'}`}
          >
            <option value="" disabled>
              Selecione a organização
            </option>
            {tenants.map((t) => (
              <option key={t.slug} value={t.slug} className="text-agnes-text-primary">
                {t.display_name}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-agnes-text-muted" />
        </div>
      </div>

      <div>
        <AgnesLabel required>E-mail</AgnesLabel>
        <AgnesInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@empresa.com"
          autoComplete="email"
          startIcon={<Mail className="h-4 w-4" />}
        />
      </div>

      <AgnesButton type="submit" variant="primary" disabled={!podeEnviar} loading={loading}>
        Enviar link
      </AgnesButton>
      <button
        type="button"
        className="text-sm text-agnes-text-muted"
        onClick={() => navigate('/login')}
      >
        Voltar ao login
      </button>
    </form>
  );
}
